import { useEffect, useState } from "react";
import { Row, Col }     from "react-bootstrap";
import { useNavigate }  from "react-router-dom";
import CardModule       from "../components/CardModule";
import { useAuth }      from "../context/AuthContext";
import { useOnlineStatus } from "../hooks/useSync";
import Swal             from "sweetalert2";
import { listarUsuariosAPI } from "../api/usuariosAPI";
import { ESTRATEGIA_COLOR } from "../../dashboard/utils/colores";
import "../styles/menu.css";

// TODO: ajustar cuando se defina qué estrategias exigen internet obligatorio
// (equivalente a REQUIEREN_INTERNET=["educacion","educacion_indigena"] en el SI-PIC)
const REQUIEREN_INTERNET = [];

export default function Home() {
  const navigate        = useNavigate();
  const { user }        = useAuth();
  const online          = useOnlineStatus();
  const rol             = user?.rol || "usuario";

  // Solo admin: cuántas solicitudes de cuenta esperan aprobación, para
  // mostrar el contador en el botón de Usuarios.
  const [pendientes, setPendientes] = useState(0);
  useEffect(() => {
    if (rol !== "admin" || !online) return;
    listarUsuariosAPI()
      .then((lista) => setPendientes(lista.filter((u) => u.estado === "pendiente").length))
      .catch(() => {});
  }, [rol, online]);


  // Estrategias de Promoción Social. El `permiso` de cada una debe
  // coincidir EXACTO con: el valor que se marca en ModalUsuario.js, el
  // `programaRequerido` de su <Route> en App.js, y su propio path.
  const estrategias = [
    { name: "RBC",                  path: "/rbc",            permiso: "rbc",             icon: "♿",  color: ESTRATEGIA_COLOR.rbc, desc: "Rehabilitación basada en comunidad" },
    { name: "Vejez",                path: "/vejez",           permiso: "vejez",           icon: "🧓",  color: ESTRATEGIA_COLOR.vejez, desc: "Atención integral a personas mayores" },
    { name: "Habitante de Calle",   path: "/habitante-calle", permiso: "habitante_calle", icon: "🏙️", color: ESTRATEGIA_COLOR.habitante_calle, desc: "Atención a población en situación de calle" },
    { name: "Población Migrante",   path: "/migrante",        permiso: "migrante",        icon: "🧭",  color: ESTRATEGIA_COLOR.migrante, desc: "Atención a población migrante" },
  ];

  const tieneAcceso = (permiso) => {
    if (rol === "admin") return true;
    if (user?.programas?.includes("TODOS")) return true;
    return !!user?.programas?.includes(permiso);
  };

  const entrarModulo = async (mod) => {
    if (!tieneAcceso(mod.permiso)) {
      Swal.fire({
        icon: "warning",
        title: "Sin acceso",
        text: "No tienes permisos para ingresar a esta estrategia.",
        confirmButtonColor: "#1a3a5c"
      });
      return;
    }

    if (!online && REQUIEREN_INTERNET.includes(mod.permiso)) {
      Swal.fire({
        icon: "info",
        title: "📡 Sin conexión",
        html: `
          <p>La estrategia <strong>${mod.name}</strong> requiere conexión a internet.</p>
        `,
        confirmButtonColor: "#1a3a5c",
        confirmButtonText: "Entendido"
      });
      return;
    }

    navigate(mod.path);
  };

  const estaDeshabilitado = (mod) => {
    if (!tieneAcceso(mod.permiso)) return true;
    if (!online && REQUIEREN_INTERNET.includes(mod.permiso)) return true;
    return false;
  };

  const getDesc = (mod) => {
    if (!tieneAcceso(mod.permiso)) return "Sin permisos asignados";
    if (!online && REQUIEREN_INTERNET.includes(mod.permiso)) return "Requiere conexión";
    return mod.desc;
  };

  // ── Ícono según estado ────────────────────────────────────────────────────
  const getIcon = (mod) => {
    if (!tieneAcceso(mod.permiso))                         return "🔒";  // sin acceso
    if (!online && REQUIEREN_INTERNET.includes(mod.permiso)) return "🔌";  // sin internet
    return mod.icon;                                                        // normal
  };

  const getColor = (mod) => {
    if (!tieneAcceso(mod.permiso))                         return "#adb5bd";
    if (!online && REQUIEREN_INTERNET.includes(mod.permiso)) return "#adb5bd";
    return mod.color;
  };

  // Sin sesión iniciada todavía (no hay login construido aún) — evita el
  // crash de leer user.nombre/user.rol sobre null.
  if (!user) {
    return (
      <div className="text-center py-5">
        <p className="text-muted">Debes iniciar sesión para ver el menú de estrategias.</p>
      </div>
    );
  }

  const primerNombre = (user.nombre || "").split(" ")[0];
  const disponibles = estrategias.filter((e) => tieneAcceso(e.permiso)).length;

  return (
    <div>
      {/* ── Bienvenida ── */}
      <section className="home-hero">
        <div className="home-hero-kicker">{rol === "admin" ? "Administrador" : "Panel de trabajo"}</div>
        <h2>Hola, {primerNombre} 👋</h2>
        <p>
          {rol === "admin"
            ? "Tienes acceso a todas las estrategias, al dashboard y a la administración de usuarios."
            : disponibles > 0
              ? `Tienes ${disponibles} estrategia${disponibles === 1 ? "" : "s"} habilitada${disponibles === 1 ? "" : "s"}. Elige una para empezar a registrar.`
              : "Aún no tienes estrategias asignadas. Comunícate con el administrador."}
        </p>

        <div className="home-acciones">
          {rol === "admin" ? (
            <>
              <button type="button" className="home-accion blanca" onClick={() => navigate("/admin")}>
                ⚙️ Usuarios
                {pendientes > 0 && <span className="contador" title="Solicitudes pendientes">{pendientes}</span>}
              </button>
              <button type="button" className="home-accion" onClick={() => navigate("/dashboard")}>
                📊 Dashboard
              </button>
            </>
          ) : (
            estrategias.filter((est) => tieneAcceso(est.permiso)).map((est) => (
              <span key={est.permiso} className="home-chip">{est.icon} {est.name}</span>
            ))
          )}
        </div>
      </section>

      {!online && (
        <div className="alert alert-warning py-2 px-3 mb-3 d-flex align-items-center gap-2"
          style={{ borderRadius: 12, fontSize: "0.85rem" }}>
          <span>📴</span>
          <div>
            <strong>Sin conexión</strong> — Trabajando en modo offline. Lo que registres se sincroniza al volver la señal.
          </div>
        </div>
      )}

      <div className="home-seccion">
        <h3>Estrategias</h3>
        <span>{estrategias.length} disponibles en el sistema</span>
      </div>

      <Row className="g-3">
        {estrategias.map((mod) => {
          const disabled    = estaDeshabilitado(mod);
          const sinInternet = !online && REQUIEREN_INTERNET.includes(mod.permiso);

          return (
            <Col key={mod.permiso} xs={12} md={6}>
              <CardModule
                title={mod.name}
                icon={getIcon(mod)}
                desc={getDesc(mod)}
                color={getColor(mod)}
                bloqueado={disabled}
                etiqueta={sinInternet && tieneAcceso(mod.permiso) ? "📡 Sin internet" : null}
                onClick={() => entrarModulo(mod)}
              />
            </Col>
          );
        })}
      </Row>
    </div>
  );
}
